import React, { useId } from 'react';

// Campo con etiqueta, ayuda y error, todo asociado por aria. Los controles nativos (input, textarea, select) reciben el foco
// visible por CSS, no por JavaScript.
export const controlClass = `w-full rounded-xl bg-raised/60 border border-line text-fg placeholder:text-fg-faint
  transition-colors duration-150 hover:border-fg/20
  focus:outline-none focus-visible:border-live focus-visible:ring-2 focus-visible:ring-live/30
  disabled:opacity-60 disabled:cursor-not-allowed aria-[invalid=true]:border-signal`;

// `adornment`: un botón dentro del campo, a la derecha (p. ej. mostrar la contraseña); el control sigue asociado a la etiqueta.
export default function Field({ label, hint, error, children, className = '', optional = false, adornment = null }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const control = React.Children.only(children);
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label htmlFor={id} className="text-[13px] font-medium text-fg">
        {label}{optional && <span className="ml-1.5 font-normal text-fg-faint">(opcional)</span>}
      </label>
      {(() => {
        const el = React.cloneElement(control, {
          id,
          'aria-describedby': [hintId, errId].filter(Boolean).join(' ') || undefined,
          'aria-invalid': error ? true : undefined,
        });
        return adornment ? <div className="relative">{el}<div className="absolute right-1.5 top-1/2 -translate-y-1/2">{adornment}</div></div> : el;
      })()}
      {hint && !error && <p id={hintId} className="text-[12.5px] leading-snug text-fg-faint">{hint}</p>}
      {error && <p id={errId} role="alert" className="text-[12.5px] leading-snug text-signal">{error}</p>}
    </div>
  );
}

export const TextInput = React.forwardRef(function TextInput({ className = '', ...rest }, ref) {
  return <input ref={ref} className={`${controlClass} h-10 px-3 text-sm ${className}`} {...rest} />;
});

export const TextArea = React.forwardRef(function TextArea({ className = '', ...rest }, ref) {
  return <textarea ref={ref} className={`${controlClass} px-3 py-2.5 text-sm leading-relaxed resize-y ${className}`} {...rest} />;
});

const ARROW = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%23908BA8'%3E%3Cpath d='M5.5 7.5l4.5 5 4.5-5'/%3E%3C/svg%3E\")";

export const NativeSelect = React.forwardRef(function NativeSelect({ className = '', style, children, ...rest }, ref) {
  return (
    <select
      ref={ref}
      className={`${controlClass} h-10 pl-3 pr-9 text-sm appearance-none ${className}`}
      style={{ backgroundImage: ARROW, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.65rem center', backgroundSize: '14px', ...style }}
      {...rest}
    >
      {children}
    </select>
  );
});
